import { assertNever } from "@glideapps/ts-necessities";
import md5 from "blueimp-md5";
import { isRight } from "fp-ts/lib/Either";
import * as iots from "io-ts";
// @ts-ignore
import utf8ByteLength from "utf8-byte-length";
import { makeStartOfDayTimeZoneAgnostic } from "../date-time/support";
import { assert, digitsAndLetters, escapeStringAsRegexp } from "./Support";

export const documentIDChars = digitsAndLetters;

export const glideAppsCollectionName = "glide-apps-v4";
export const glideAppsDataCollectionName = "glide-apps-v4-data";
export const glideAppsDataVersionCollectionName = "glide-apps-v4-data-versions";
export const glideAppsMetadataCollectionName = "glide-apps-v4-metadata";
export const builderAppsCollectionName = "builder-apps-v1";
export const builderActionsCollectionName = "builder-actions";
export const tileDescriptionsCollectionName = "tile-descriptions-v1";
export const usersCollectionName = "users-v1";
export const actionsCollectionName = "actions";
export const actionsMetadataCollectionName = "actions-metadata";
export const appAnalyticsCollectionName = "analytics";
export const userAnalyticsCollectionName = "analytics-user";
export const globalSettingsCollectionName = "global-settings";
export const jobsCollectionName = "jobs";
export const newFeaturesCollectionName = "new-features";
export const newFeaturesSeenCollectionName = "new-features-seen";
export const userAppUsersCollectionName = "user-app-users";
export const geocodingCollectionName = "geocoding";
export const appsGrandfatheringCollectionName = "grandfather-apps";
export const appLoginsCollectionName = "app-logins";
export const builderAppLocksCollectionName = "builder-app-locks";
export const reloadsCollectionName = "reloads";
export const ownerAppIssuesCollectionName = "owner-app-issues";
export const orgPrivateCollectionName = "org-private";
export const invitesCollectionName = "invites";
export const orgPlansCollectionName = "org-plans";
export const videosCollectionName = "videos";
export const templateSubmissionsCollectionName = "template-submissions";
export const cronJobsCollectionName = "cron-jobs";
export const nativeTablesCollectionName = "native-tables";
export const supportCodesCollectionName = "support-codes";
export const virtualEmailsCollectionName = "virtual-emails";
export const deleteCollectionsCollectionName = "delete-collections";
export const emailsCollectionName = "emails";
export const publishedAppLocksCollectionName = "glide-apps-v4-publish-locks";
export const stripeUpgradeLocksCollectionName = "unified-stripe-upgrades";
export const freeAppsCollectionName = "user-free-apps";
export const virtualEmailsMetadataCollectionName = "virtual-emails-metadata";
export const mediaLibraryCollectionName = "media-library";
export const apiKeysCollectionName = "api-keys";
export const aiPromptsCollectionName = "ai-prompts-results";

export const ownerAppUserIDsFieldName = "$ownerAppUserIDs";
export const appUserIDFieldName = "appUserID";
export const rowVersionFieldName = "$rowVersion";

export const uniqueAppUsersWindowDays = 28;
export const uniqueAppUsersWindowDuration = uniqueAppUsersWindowDays * 24 * 60 * 60 * 1000;

export const uniqueUsersFieldName = `last${uniqueAppUsersWindowDays}DayUniqueAppUsers`;

export const purchasesCollectionName = "purchases";
export const appUsageCountersCollectionName = "app-usage-counters";

export const zapierDocName = "zapier";
export const integrationsCollectionName = "integrations";

export const inAppPathComponent = "in-app";

export const appReadWriteCountsCollectionName = "frontend-analytics";
const appReadWriteCountsSubcollectionName = "firestore-read-write";

export const supportAccessSubcollectionName = "support-access";

export const userProfilesDocumentPath = "user-profiles";
export const allowedEmailsDocumentName = "allowed-emails";

export const dataPrivacyRequestsCollectionName = "data-privacy-requests";

export const neverRefreshDocumentName = "never-refresh";

export const notebooksCollectionName = "notebooks";

export const nativeTableRowIDColumnName = "$rowID";

export function inAppPurchasesCollection(appID: string): string {
    return `${purchasesCollectionName}/${appID}/${inAppPathComponent}`;
}

export function makeInAppPurchaseActionPath(appID: string, purchaseID: string): string {
    return `${inAppPurchasesCollection(appID)}/${purchaseID}`;
}

export function appDailyUsageCountersCollectionForAppID(appID: string): string {
    return `${appUsageCountersCollectionName}/${appID}/usage`;
}

const appUserIDsDocumentNamePrefix = "emails";
// NOTE: We've come dangerously close to the prior 40,000 limit so we're doubling
// this to 8. It realistically cannot go above 10, and might not even hit 10, due
// to Firestore's 10 MB transaction limit.
//
// There is a slight cost associated with this number being 8 instead of 4. Every
// time we read the "aggregate" user profile mapping, we delete documents with
// higher indexes than the ones we saw, up to this number. We could remove
// that cost by keeping track of what the highest index was that we read, and
// then when we save only delete up to that index.
export const maxAppUserIDDocuments = 8;
// We overflowed at 16k once, so this is fairly conservative.
export const numAppUserIDsPerDocument = 10000;

export function makeAppUserIDsDocumentName(i: number): string {
    assert(i >= 0 && i < maxAppUserIDDocuments);
    if (i === 0) {
        // This must be compatible with the existing documents from when there
        // was only one, so we can't append a number.
        return appUserIDsDocumentNamePrefix;
    } else {
        return `${appUserIDsDocumentNamePrefix}-${i}`;
    }
}

export function isAppUserIDsDocumentName(name: string): boolean {
    return name.startsWith(appUserIDsDocumentNamePrefix);
}

export function documentIDForUsageCounterTime(t: Date): string {
    // We expect this to have the format of e.g. 2019-08-14T23:32:24.939Z
    // We only want everything up to the T. Thankfully, this is a standardized
    // format so we can do this awful nasty thing.
    const asString = makeStartOfDayTimeZoneAgnostic(t).toISOString();
    return asString.substring(0, asString.indexOf("T"));
}

export function appBihourlyUsageCountersAccessForAppID(
    appID: string,
    startDate: Date
): { collection: string; documentID: string } {
    const startOfDay = makeStartOfDayTimeZoneAgnostic(startDate);
    // Because getTime() is always in UTC, we don't have to convert startDate to anything timezone agnostic.
    // This is still all correct with respect to UTC.
    const millisecondOffset = startDate.getTime() - startOfDay.getTime();
    const millisecondBucket = millisecondOffset - (millisecondOffset % (30 * 60 * 1000));
    return {
        collection: `${appDailyUsageCountersCollectionForAppID(appID)}/${documentIDForUsageCounterTime(
            startDate
        )}/bihourly`,
        documentID: millisecondBucket.toString(),
    };
}

export const zapsSubcollectionName = "zaps";

export function zapsCollectionForAppID(appID: string): string {
    return `${glideAppsDataCollectionName}/${appID}/${zapsSubcollectionName}`;
}

export function appIntegrationsCollection(appID: string): string {
    return `${glideAppsDataCollectionName}/${appID}/${integrationsCollectionName}`;
}

export function dataChangelogCollection(appID: string): string {
    return `${glideAppsDataCollectionName}/${appID}/changelog`;
}

export function actionsMetadataCollectionForDevice(deviceID: string): string {
    return `${actionsMetadataCollectionName}/${deviceID}/actions`;
}

export function frontendAnalyticsCollectionName(isoDateString: string): string {
    return `${appReadWriteCountsCollectionName}/${isoDateString}/${appReadWriteCountsSubcollectionName}`;
}

export const tableNameCodec = iots.type({
    name: iots.string,
    isSpecial: iots.boolean,
});
export type TableName = iots.TypeOf<typeof tableNameCodec>;

export function isTableName(x: unknown): x is TableName {
    return isRight(tableNameCodec.decode(x));
}

export const nativeTableNameCodec = iots.type({
    isNativeTableName: iots.literal(true),
    nativeTableID: iots.string,
    // Just to make compatibility to `TableName` easier
    isSpecial: iots.literal(false),
});
export type NativeTableName = iots.TypeOf<typeof nativeTableNameCodec>;

export function isNativeTableName(x: unknown): x is NativeTableName {
    return isRight(nativeTableNameCodec.decode(x));
}

export function makeNativeTableName(nativeTableID: string): NativeTableName {
    return { isNativeTableName: true, isSpecial: false, nativeTableID };
}

export const universalTableNameCodec = iots.union([tableNameCodec, nativeTableNameCodec]);
export type UniversalTableName = iots.TypeOf<typeof universalTableNameCodec>;

export function areTableNamesEqual(n1: UniversalTableName | undefined, n2: UniversalTableName | undefined): boolean {
    if (n1 === undefined || n2 === undefined) return false;
    if (isTableName(n1)) {
        if (!isTableName(n2)) return false;
        return n1.name === n2.name && n1.isSpecial === n2.isSpecial;
    } else if (isNativeTableName(n1)) {
        if (!isNativeTableName(n2)) return false;
        return n1.nativeTableID === n2.nativeTableID;
    } else {
        return assertNever(n1);
    }
}

export const commentsTableName: TableName = { name: "comments", isSpecial: true };

export function getDebugPrintTableName(n: UniversalTableName): string {
    if (isTableName(n)) {
        return n.name;
    } else if (isNativeTableName(n)) {
        return `native table ${n.nativeTableID}`;
    } else {
        return assertNever(n);
    }
}

export function getNativeTableIDFromTableName(n: TableName): string | undefined {
    const prefix = "native-table-";
    if (!n.name.startsWith(prefix)) return undefined;
    return n.name.substring(prefix.length);
}

export function isSample(appID: string): boolean {
    return appID.startsWith("sample-") || appID.endsWith("-template");
}

// https://firebase.google.com/docs/firestore/quotas#collections_documents_and_fields
// We replace all whitespace, "/", ".", "_", and URL-encoded reserved characters with
// single space characters, trim the result, and replace all single space characters
// with dashes. The non-whitespace replacements ensure that the resulting slug is compliant
// with Cloud Firestore document ID requirements.
export function appIDForTemplateFromTitle(title: string): string {
    const computedSlug = title
        .replace(/([\s/.?_;,:@&=+$#])+/g, " ")
        .trim()
        .replace(/ /g, "-")
        .toLowerCase();
    return `${computedSlug}-template`;
}

// https://firebase.google.com/docs/firestore/quotas#collections_documents_and_fields

// Cannot contain a newline character (this is not documented)

// Cannot contain a forward slash (/)

// Cannot solely consist of a single period (.) or double periods (..)
// The Firestore web UI is more strict here: it can't start with a
// period and can't contain double periods.  Not documented: it also
// can't end with a period.
// Since we sometimes use `updateNested`, any period will introduce a
// map, so we just disallow them completely.

// Also, field names can, but shouldn't, include dots:
// https://github.com/quicktype/glide/issues/5521

// Brackets, asterisks, and tildes aren't allowed, either:
// https://github.com/quicktype/glide/issues/8410
const invalidCharRegexp = /[*~\n/.[\]]/;

export function isLegalFirebaseName(id: string, byteLimit: number): boolean {
    if (id === "") return false;

    if (invalidCharRegexp.test(id)) return false;

    // Cannot match the regular expression __.*__
    if (id.startsWith("__") && id.endsWith("__")) return false;

    // Must be no longer than 1,500 bytes
    if (utf8ByteLength(id) > byteLimit) return false;

    // FIXME: Check: Must be valid UTF-8 characters

    return true;
}

export function templateIDForAppID(appID: string): string {
    return `${appID}-template`;
}

const documentIDsForTableNames = new Map<string, string>();

const documentIDByteLimit = 1500;

export function documentIDForTableName(tableName: TableName): string {
    const { name, isSpecial } = tableName;

    if (isSpecial) {
        return "$" + name;
    }

    const cachedID = documentIDsForTableNames.get(name);
    if (cachedID !== undefined) return cachedID;

    let id =
        "_" +
        Array.from(name)
            .map(c => {
                if (c === "/") return "#_";
                if (c === "#") return "##";
                return c;
            })
            .join("");
    if (!isLegalFirebaseName(id, documentIDByteLimit)) {
        id = md5(id);
        assert(isLegalFirebaseName(id, documentIDByteLimit));
    }

    documentIDsForTableNames.set(name, id);
    return id;
}

export const tablesSubcollectionName = "tables";

export function makeTablesPath(appID: string): string {
    return `${glideAppsDataCollectionName}/${appID}/${tablesSubcollectionName}`;
}

export function tableNameAsDocumentID(nameOrDocumentID: string | TableName): string {
    if (typeof nameOrDocumentID === "string") {
        assert(nameOrDocumentID !== "");
        return nameOrDocumentID;
    } else {
        return documentIDForTableName(nameOrDocumentID);
    }
}

export function makeRoleHash(role: string): string | undefined {
    role = role.trim().toLowerCase();
    if (role === "") return undefined;
    return md5(role);
}

export type MakeRowsPath = (tablesPath: string, nameOrDocumentID: string | TableName) => string;

export const publicRowsSubcollectionName = "rows";
export const privateRowsSubcollectionName = "private-rows";
export const userRowsSubcollectionName = "user-rows";
export const preliminaryRowsCleanupSubcollectionName = "preliminary-cleanup";

export function makePublicRowsPath(tablesPath: string, nameOrDocumentID: string | TableName): string {
    return `${tablesPath}/${tableNameAsDocumentID(nameOrDocumentID)}/${publicRowsSubcollectionName}`;
}

export function makePrivateRowsPath(tablesPath: string, nameOrDocumentID: string | TableName): string {
    return `${tablesPath}/${tableNameAsDocumentID(nameOrDocumentID)}/${privateRowsSubcollectionName}`;
}

export function makePreliminaryCleanupPath(tablesPath: string, nameOrDocumentID: string | TableName): string {
    return `${tablesPath}/${tableNameAsDocumentID(nameOrDocumentID)}/${preliminaryRowsCleanupSubcollectionName}`;
}

export function makeUserRowsPath(tablesPath: string, tableName: TableName): string {
    return `${tablesPath}/${documentIDForTableName(tableName)}/${userRowsSubcollectionName}`;
}

export function makeCommentsPath(appID: string): string {
    return makePublicRowsPath(makeTablesPath(appID), commentsTableName);
}

export const loginLogSubcollectionName = "login-log";
export const loginSummaryPathComponent = "login-summary";

export function makeLoginLogPath(appID: string): string {
    return `${glideAppsMetadataCollectionName}/${appID}/${loginLogSubcollectionName}`;
}

export function makeLoginSummaryPath(appID: string): string {
    return `${glideAppsMetadataCollectionName}/${appID}/${loginSummaryPathComponent}`;
}

export enum ActionKind {
    AddRowToTable = "add-row-to-table",
    SetColumnsInRow = "set-columns-in-row",
    DeleteRow = "delete-row",
    WritePurchaseToSheet = "purchases",
    DeleteColumn = "delete-column",
}

export function isBasicTableMutation(kind: ActionKind): boolean {
    return kind === ActionKind.AddRowToTable || kind === ActionKind.SetColumnsInRow || kind === ActionKind.DeleteRow;
}

export function makeActionDocumentPath(appID: string, kind: ActionKind): string {
    return `${actionsCollectionName}/${appID}/${kind}`;
}

const actionKindRegexp = [
    ActionKind.AddRowToTable,
    ActionKind.SetColumnsInRow,
    ActionKind.DeleteRow,
    ActionKind.DeleteColumn,
]
    .map(escapeStringAsRegexp)
    .join("|");

const documentIDMatch = "([^/]+)";

const actionDocumentPathRegexp = new RegExp(
    `^(${escapeStringAsRegexp(actionsCollectionName)}/${documentIDMatch}/(${actionKindRegexp}))/${documentIDMatch}$`
);

const purchasesDocumentPathRegexp = new RegExp(
    `^(${escapeStringAsRegexp(purchasesCollectionName)}/${documentIDMatch}/in-app)/${documentIDMatch}$`
);

export type ActionDocumentMetadata = Readonly<{
    kind: ActionKind;
    appID: string;

    collection: string;
    docID: string;
    // the full path, i.e. `${collection}/${docID}`
    actionPath: string;
}>;

export function parseActionDocumentPath(path: string): ActionDocumentMetadata | undefined {
    let match = path.match(actionDocumentPathRegexp);
    if (match !== null) {
        return {
            appID: match[2],
            kind: match[3] as ActionKind,
            collection: match[1],
            docID: match[4],
            actionPath: path,
        };
    }

    match = path.match(purchasesDocumentPathRegexp);
    if (match !== null) {
        return {
            appID: match[2],
            kind: ActionKind.WritePurchaseToSheet,
            collection: match[1],
            docID: match[3],
            actionPath: path,
        };
    }

    return undefined;
}

export const builderAppsBackupsSubcollectionName = "backups";

export function makeBuilderAppBackupsPath(appID: string): string {
    return `${builderAppsCollectionName}/${appID}/${builderAppsBackupsSubcollectionName}`;
}

export function makeAppUsersForAppPath(appID: string): string {
    return `${glideAppsDataCollectionName}/${appID}/app-users`;
}

export function makeUserProfileDocumentPathForApp(appID: string): string {
    return `${glideAppsDataCollectionName}/${appID}/${userProfilesDocumentPath}`;
}

export function makeNotebooksDocumentPathForApp(appID: string): string {
    return `${notebooksCollectionName}/${appID}/for-table`;
}
